import { Revenue } from "./types";
import { AppError } from "webrtc-player/common/app_error";
import CryptoJS from "crypto-js";
import { fetchPostWithParams, sendHttpRequest } from "lib/fetcher";
import { showToast } from "ui/components/common";
import IndexDB from "lib/indexDB";
import {
  ALERT_STATUS_DISPLAY,
  ALERT_PROCESS_STATUS_SDK,
  INVESTIGATE_TAG_DISPLAY,
  INVESTIGATE_TAG_SDK,
  CASE_STATUS_DISPLAY,
  CASE_STATUS_SDK,
  onMotorVehicleKey,
  personKey,
  vehicleKey,
  conflagrationKey,
  smokingKey,
  USER_ROLE,
} from "lib/constants";
import ColorHash from "color-hash";
import { v4 as uuid } from "uuid";
import {
  googleSSOClientId,
  microsoftSSOClientId,
  IdentifiedServerError,
} from "lib/constants";
import { SSOType, SSOPage } from "pages/dashboard/sso/page";
import {
  parsePhoneNumberFromString,
  parsePhoneNumber,
} from "libphonenumber-js";
import Cookies from "js-cookie";
import { ExternalErrorType } from "lib/types";

export const colorHash = new ColorHash({ lightness: [0.35, 0.5, 0.65] });

export const softTitle = () => {
  if (window.location.hostname.includes("command.aplusguard.com")) {
    return "A Plus Guard";
  }
  return "Alpha Vision";
};

export const formatCurrency = (amount: number) => {
  return (amount / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

export function checkRules(detections) {
  for (let i = 0; i < detections.length; i++) {
    if (detections[i]?.region?.polygon?.points?.length >= 3) {
      continue;
    } else {
      return false;
    }
  }
  return true;
}

export function checkLineRules(detections) {
  for (let i = 0; i < detections.length; i++) {
    if (
      detections[i]?.region?.line?.endPoint &&
      detections[i]?.region?.line?.startPoint
    ) {
      continue;
    } else {
      return false;
    }
  }
  return true;
}

export const formatDateToLocal = (
  dateStr: string,
  locale: string = "en-US",
) => {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(date);
};

export const generateYAxis = (revenue: Revenue[]) => {
  // Calculate what labels we need to display on the y-axis
  // based on highest record and in 1000s
  const yAxisLabels = [];
  const highestRecord = Math.max(...revenue.map((month) => month.revenue));
  const topLabel = Math.ceil(highestRecord / 1000) * 1000;

  for (let i = topLabel; i >= 0; i -= 1000) {
    yAxisLabels.push(`$${i / 1000}K`);
  }

  return { yAxisLabels, topLabel };
};

export const generatePagination = (currentPage: number, totalPages: number) => {
  // If the total number of pages is 7 or less,
  // display all pages without any ellipsis.
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // If the current page is among the first 3 pages,
  // show the first 3, an ellipsis, and the last 2 pages.
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages - 1, totalPages];
  }

  // If the current page is among the last 3 pages,
  // show the first 2, an ellipsis, and the last 3 pages.
  if (currentPage >= totalPages - 2) {
    return [1, 2, "...", totalPages - 2, totalPages - 1, totalPages];
  }

  // If the current page is somewhere in the middle,
  // show the first page, an ellipsis, the current page and its neighbors,
  // another ellipsis, and the last page.
  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

// When the service returns success, the response is {}
export const resSuccessful = (respronse: any) => {
  return Object.keys(respronse).length === 0;
};

// argon2id hash alg. The parameters inside should not be modified
export const argon2id = async (
  password: string,
  salt: string,
  keyLen: number,
) => {
  // @ts-ignore
  const { hashHex } = await argon2.hash({
    pass: password,
    salt: salt,
    time: 1, // the number of iterations
    mem: 46 * 1024, // used memory, in KiB
    hashLen: keyLen, // desired hash length
    parallelism: 1, // desired parallelism (it won't be computed in parallel, however)
    // @ts-ignore
    type: argon2.ArgonType.Argon2id, // Argon2d, Argon2i, Argon2id
  });
  return hashHex;
};

export function formatMinute(msTimestamp: number) {
  const hours = new Date(msTimestamp).getHours();
  const _12Hour = hours > 12 ? hours - 12 : hours;
  const hourStr = _12Hour < 10 ? "0" + _12Hour : _12Hour;
  const minutes = new Date(msTimestamp).getMinutes();
  const mintueStr = minutes % 60 < 10 ? "0" + minutes : minutes;
  return hourStr + ":" + mintueStr + (hours >= 12 ? " PM" : " AM");
}

// iv + aes(pwd)
export function aesEncrypt(data, key, iv) {
  const _key = CryptoJS.enc.Utf8.parse(key);
  const _iv = CryptoJS.enc.Utf8.parse(iv);

  const cipher = CryptoJS.AES.encrypt(data, _key, {
    iv: _iv, // parse the IV
    padding: CryptoJS.pad.Pkcs7,
    mode: CryptoJS.mode.CBC,
  });

  const hexString = cipher.ciphertext.toString(CryptoJS.enc.Hex);
  const ivHex = CryptoJS.enc.Hex.stringify(_iv);
  const str = CryptoJS.enc.Base64.stringify(
    CryptoJS.enc.Hex.parse(ivHex + hexString),
  );

  console.log("after", str);

  return str;
}

export function aesDecrypt(encryptedData, key) {
  const _key = CryptoJS.enc.Utf8.parse(key);

  // Base64 decode the encrypted data
  const encryptedHexStr = CryptoJS.enc.Base64.parse(encryptedData).toString(
    CryptoJS.enc.Hex,
  );

  // Extract the IV from the encrypted data
  const ivHex = encryptedHexStr.substring(0, 32);
  const encryptedHex = encryptedHexStr.substring(32);

  const _iv = CryptoJS.enc.Hex.parse(ivHex);

  // Decrypt the data
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext: CryptoJS.enc.Hex.parse(encryptedHex) },
    _key,
    {
      iv: _iv,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC,
    },
  );

  // Convert the decrypted data to a UTF-8 string
  const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

  console.log("decrypted", decryptedStr);

  return decryptedStr;
}

export function getCurrencySymbol(currency: string) {
  switch (currency) {
    case "usd":
      return "$";
    case "eur":
      return "€";
    case "bgp":
      return "£";
    case "jpy":
      return "¥";
    case "cny":
      return "¥";
    default:
      return "$";
  }
}

export const transFileSize = (size: string | undefined | number) => {
  const sizeNum = Number(size);
  if (isNaN(sizeNum) || sizeNum < 0) {
    return "-";
  }

  if (sizeNum < 1024) {
    return sizeNum + "B";
  }
  if (sizeNum < 1000 * 1024) {
    return (Math.floor((sizeNum * 100) / 1024) / 100).toFixed(1) + "KB";
  }
  if (sizeNum < 1000 * 1024 * 1024) {
    return (Math.floor((sizeNum * 100) / 1024 / 1024) / 100).toFixed(1) + "MB";
  }
  if (sizeNum < 1000 * 1024 * 1024 * 1024) {
    return (
      (Math.floor((sizeNum * 100) / 1024 / 1024 / 1024) / 100).toFixed(1) + "GB"
    );
  }
  if (sizeNum < 1000 * 1024 * 1024 * 1024 * 1024) {
    return (
      (Math.floor((sizeNum * 100) / 1024 / 1024 / 1024 / 1024) / 100).toFixed(
        1,
      ) + "TB"
    );
  }

  return "-";
};
export function downloadTextAsFile(text, filename) {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
export function copyToClipBoard(v) {
  const tempInput = document.createElement("input");
  tempInput.value = v;
  document.body.appendChild(tempInput);
  tempInput.select();
  document.execCommand("copy");
  document.body.removeChild(tempInput);
  showToast(0, "The text has been copied", { minWidth: 200 });
}
export class dbUtil {
  db: IndexDB = null;
  constructor(db: IndexDB) {
    this.db = db;
  }
  getAllKeys = async function (tableName) {
    try {
      const keys = await this.db.getAllKeys(tableName);
      return keys;
    } catch (error) {
      console.error(error);
    }
  };
  delete = async function (tableName, key) {
    try {
      await this.db.delete(tableName, key);
    } catch (error) {
      console.error(error);
    }
  };
  put = async function (tableName, data) {
    try {
      Object.assign(data, { __updateTime: Date.now() });
      await this.db.putObject(tableName, data);
    } catch (error) {
      console.warn(error);
      throw error;
    }
  };
  read = async function (tableName, key) {
    try {
      const data = await this.db.getObject(tableName, key);
      if (!data) {
        return;
      }
      return data;
    } catch (error) {
      console.error(error);
    }
  };
  deleteMatchData = async function (tableName, recordId, fileId?) {
    try {
      await this.db.deleteMatchData(tableName, recordId, fileId);
    } catch (error) {
      console.error(error);
    }
  };

  close = function () {
    this.db.closeDB();
  };
}
// export async function putDB(db, tableName, data) {
//   try {
//     await db.putObject(tableName, data);
//   } catch (error) {
//     console.error(error);
//   }
// }

// export async function readDB(db, tableName, key) {
//   try {
//     const data = await db.getObject(tableName, key);
//     if (!data) {
//       return;
//     }
//     return data;
//     // setIndexDBBlob((data as { imageBlob: Blob })?.imageBlob);
//   } catch (error) {
//     console.error(error);
//   }
// }

const tokenMap = new Map();
export const getLocalStroageByKeyId = (key: string, id: string) => {
  const value = JSON.parse(localStorage.getItem(key));
  if (!value) {
    return null;
  }
  const data = value[`${id}`];
  if (!data) {
    return null;
  }
  return JSON.parse(data);
};
export const appenToLocalStroageByKeyId = (
  key: string,
  id: string,
  data: any,
) => {
  const origin = JSON.parse(localStorage.getItem(key)) || {};
  origin[`${id.toString()}`] =
    typeof data === "object" ? JSON.stringify(data) : data;
  localStorage.setItem(key, JSON.stringify(origin));
};
export const getTokenByActionAndResourceId = async (
  actions: string[],
  ResourceId,
  user,
  resourceType,
  orgId?,
  otherId?,
) => {
  const arriedResourceId = Array.isArray(ResourceId)
    ? ResourceId
    : [ResourceId];
  const oldToken = tokenMap.get(
    `${actions.join(",")}-${arriedResourceId.join(",")}`,
  );
  //return old token if it is not expire
  console.log("oldToken", oldToken, tokenMap);
  if (oldToken && oldToken.token && oldToken.expire > new Date().getTime()) {
    return oldToken.token;
  } else {
    //get token and refresh token cache
    const resourceIds = arriedResourceId.map((resId: string | number) =>
      typeof resId === "string" ? { sid: resId, ...otherId } : { id: resId },
    );
    const postDownloadTokenData = {
      data: [
        {
          actions: actions,
          resourceIds: resourceIds,
          resourceType: resourceType,
        },
      ],
    };
    const { data: token } = await fetchPostWithParams(
      "/perm/perms/third",
      user.token,
      orgId || user.orgId,
      postDownloadTokenData,
    );
    const defaultExpired = new Date().getTime() + 60 * 1000 * 5;
    tokenMap.set(`${actions.join(",")}-${arriedResourceId.join(",")}`, {
      token,
      expire:
        token?.length > 0 ? token[0]?.expiration - 60 * 1000 : defaultExpired,
    });
    return token;
  }
};
export const formatContextDate = (
  dateTime: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today.getTime() - 86400000);

  const date = new Date(dateTime);
  const refDate = new Date(dateTime);
  refDate.setHours(0, 0, 0, 0);

  if (refDate.getTime() === today.getTime()) {
    options = {
      month: "short",
      day: "2-digit",
    };
    return `Today, ${date.toLocaleDateString("en-US", options as Intl.DateTimeFormatOptions)}`;
  } else if (refDate.getTime() === yesterday.getTime()) {
    options = {
      month: "short",
      day: "2-digit",
    };
    return `Yesterday, ${date.toLocaleDateString("en-US", options as Intl.DateTimeFormatOptions)}`;
  } else {
    if (!options) {
      options = {
        month: "short",
        day: "2-digit",
        year: "numeric",
      };
    }
    return date.toLocaleDateString("en-US", {
      ...(options as Intl.DateTimeFormatOptions),
      // weekday: "short",
    });
  }
};

export const formatContextDatetime = (
  dateTime: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today.getTime() - 86400000);

  const date = new Date(dateTime);
  const refDate = new Date(dateTime);
  refDate.setHours(0, 0, 0, 0);

  if (refDate.getTime() === today.getTime()) {
    options = {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    return `Today, ${date.toLocaleDateString("en-US", options as Intl.DateTimeFormatOptions)}`;
  } else if (refDate.getTime() === yesterday.getTime()) {
    options = {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    };
    return `Yesterday, ${date.toLocaleDateString("en-US", options as Intl.DateTimeFormatOptions)}`;
  } else {
    if (!options) {
      options = {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        year: "numeric",
      };
    }
    return date.toLocaleDateString("en-US", {
      ...(options as Intl.DateTimeFormatOptions),
      // weekday: "short",
    });
  }
};

export const formatDatetimeRange = (startDate: Date, endDate: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  };
  let endStr = "";
  if (startDate.toDateString() === endDate.toDateString()) {
    endStr = endDate.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
    });
  } else {
    endStr = endDate.toLocaleDateString("en-US", options);
  }

  return `${formatContextDatetime(startDate, options)} - ${endStr}`;
};

export function transformEnum<T>(
  srcEnumObj: any,
  desEnumObj: T,
  value: string,
) {
  const keys = Object.keys(srcEnumObj) as Array<keyof typeof srcEnumObj>;
  const srcKey = keys.find((k) => srcEnumObj[k] === value);
  if (!srcKey) {
    return value;
  }
  const desKey = desEnumObj[srcKey as keyof typeof desEnumObj] as T;

  return desKey || value;
}

export const formatAlertStatusDisplayToSDK = (status) => {
  return transformEnum<typeof ALERT_PROCESS_STATUS_SDK>(
    ALERT_STATUS_DISPLAY,
    ALERT_PROCESS_STATUS_SDK,
    status,
  ) as ALERT_PROCESS_STATUS_SDK;
};

export const formatAlertStatusSDKToDisplay = (
  status: ALERT_PROCESS_STATUS_SDK,
) => {
  return transformEnum<typeof ALERT_STATUS_DISPLAY>(
    ALERT_PROCESS_STATUS_SDK,
    ALERT_STATUS_DISPLAY,
    status,
  ) as ALERT_STATUS_DISPLAY;
};

export const formatAlertTagDisplayToSDK = (status: INVESTIGATE_TAG_DISPLAY) => {
  return transformEnum<typeof INVESTIGATE_TAG_SDK>(
    INVESTIGATE_TAG_DISPLAY,
    INVESTIGATE_TAG_SDK,
    status,
  ) as INVESTIGATE_TAG_SDK;
};

export const formatAlertTagSDKToDisplay = (status: INVESTIGATE_TAG_SDK) => {
  return transformEnum<typeof INVESTIGATE_TAG_DISPLAY>(
    INVESTIGATE_TAG_SDK,
    INVESTIGATE_TAG_DISPLAY,
    status,
  ) as INVESTIGATE_TAG_DISPLAY;
};

export const formatAlertTagSDKToDisplayWithDesc = (
  status: INVESTIGATE_TAG_SDK,
  desc: string,
) => {
  if (status === INVESTIGATE_TAG_SDK.Others) {
    return desc || INVESTIGATE_TAG_DISPLAY.Others;
  }
  return transformEnum<typeof INVESTIGATE_TAG_DISPLAY>(
    INVESTIGATE_TAG_SDK,
    INVESTIGATE_TAG_DISPLAY,
    status,
  ) as INVESTIGATE_TAG_DISPLAY;
};

export const formatCaseStatusSDKToDisplay = (status: CASE_STATUS_SDK) => {
  return transformEnum<typeof CASE_STATUS_DISPLAY>(
    CASE_STATUS_SDK,
    CASE_STATUS_DISPLAY,
    status,
  ) as CASE_STATUS_DISPLAY;
};

export const formatCaseStatusDisplayToSDK = (status: CASE_STATUS_DISPLAY) => {
  return transformEnum<typeof CASE_STATUS_SDK>(
    CASE_STATUS_DISPLAY,
    CASE_STATUS_SDK,
    status,
  ) as CASE_STATUS_SDK;
};

export const getAlertStatusStyles = (status: ALERT_PROCESS_STATUS_SDK) => {
  switch (status) {
    case ALERT_PROCESS_STATUS_SDK.Todo:
      return {
        backgroundColor: "#EEEEEE",
        color: "#717171",
      };
    case ALERT_PROCESS_STATUS_SDK.FalseAlarm:
      return {
        backgroundColor: "#E4F3F5",
        color: "#0098A7",
      };
    case ALERT_PROCESS_STATUS_SDK.Close:
      return {
        backgroundColor: "#EBF1FD",
        color: "#3E83F6",
      };
    case ALERT_PROCESS_STATUS_SDK.Investigate:
      return { backgroundColor: "#FCF8E3", color: "#974F0C" };
    default:
      return { backgroundColor: "#FFFFFF", color: "#000000" };
  }
};

export const getCaseStatusStyles = (
  status: CASE_STATUS_SDK | "Case Detected",
) => {
  switch (status) {
    case CASE_STATUS_SDK.Resolved:
      return {
        backgroundColor: "#E4F6EC",
        color: "#3A855D",
      };
    case CASE_STATUS_SDK.Close:
      return {
        backgroundColor: "#DCE7FD",
        color: "#3E83F6",
      };
    case CASE_STATUS_SDK.Investigate:
      return { backgroundColor: "#FFF7D6", color: "#974F0C" };
    case CASE_STATUS_SDK.CallPd:
      return { backgroundColor: "#F3F0FF", color: "#5E4DB2" };
    case "Case Detected":
      return { backgroundColor: "#E4F3F5", color: "#0098A7" };
    default:
      return { backgroundColor: "#FFFFFF", color: "#000000" };
  }
};

export function idelPerformTasks(tasks) {
  const scheduler = (chunkTask) => {
    requestIdleCallback((deadline) => {
      chunkTask(() => deadline.timeRemaining() > 0);
    });
  };
  performTasks(tasks, scheduler);
}

export function performTasks(tasks, scheduler) {
  if (tasks?.length === 0) {
    return;
  }
  let i = 0;
  function _run() {
    scheduler((goOn) => {
      while (i < tasks?.length && goOn()) {
        tasks[i]?.fun(...(tasks[i]?.args as any));
        i++;
      }
      _run();
    });
  }
  _run();
}

export const getCaseStatusBgColor = (
  status: CASE_STATUS_SDK | "Case Detected",
) => {
  switch (status) {
    case "Case Detected":
      return "bg-[#BDBDBD]";
    case CASE_STATUS_SDK.Investigate:
      return "bg-[#FF9800]";
    case CASE_STATUS_SDK.CallPd:
      return "bg-[#5E4DB2]";
    case CASE_STATUS_SDK.Resolved:
      return "bg-[#3A855D]";
    case CASE_STATUS_SDK.Close:
      return "bg-[#3E83F6]";
    default:
      return "bg-[#BDBDBD]";
  }
};

export const getCaseStatusTextColor = (
  status: CASE_STATUS_SDK | "Case Detected",
) => {
  switch (status) {
    case "Case Detected":
      return "text-[#222222]";
    case CASE_STATUS_SDK.Investigate:
      return "text-[#FF9800]";
    case CASE_STATUS_SDK.CallPd:
      return "text-[#5E4DB2]";
    case CASE_STATUS_SDK.Resolved:
      return "text-[#3A855D]";
    case CASE_STATUS_SDK.Close:
      return "text-[#3E83F6]";
    default:
      return "text-[#222222]";
  }
};
export function getEventText(type: string, plateNo?: string) {
  switch (type) {
    case personKey:
      return "People";
    case vehicleKey:
      if (plateNo) {
        return plateNo;
      }
      return "Vehicle";
    case onMotorVehicleKey:
      return "Non-Motor";
    case conflagrationKey:
      return "Conflagration";
    case smokingKey:
      return "Smoking";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
}
export async function imageBitmapToBlob(
  imageBitmap: ImageBitmap,
): Promise<Blob> {
  // Create an OffscreenCanvas with the same dimensions as the ImageBitmap
  const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("2D context not available");
  }

  // Draw the ImageBitmap onto the canvas
  context.drawImage(imageBitmap, 0, 0);

  // Convert the canvas to a Blob
  const blob = await canvas.convertToBlob();
  return blob;
}
export const downloadVideoUrl = async (
  url: string,
  filename: string = "video.mp4",
  newWindow: boolean = false,
) => {
  if (newWindow) {
    const windowRef = window.open("", "_blank");

    windowRef.document.write("<html><body>Downloading...</body></html>");
    windowRef.document.close();

    const link = windowRef.document.createElement("a");
    link.href = url;
    link.download = filename || "video.mp4";
    link.click();
    setTimeout(() => {
      windowRef.close();
    }, 2000);
    return;
  } else {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {},
      });
      console.log("Response:", response);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename || "video.mp4");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      showToast(
        1,
        "There was an error downloading the video. Please try again later.",
      );
    }
  }
};

export const downloadFromSrc = async (name: string, blobSrc: string) => {
  const link = document.createElement("a");
  link.href = blobSrc;
  link.download = name;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
};

export const captureFramesFromVideo = async (url: string): Promise<string> => {
  const video = document.createElement("video");
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  video.src = url;
  video.crossOrigin = "anonymous";

  await new Promise((resolve) => {
    video.addEventListener("loadeddata", () => {
      video.currentTime = 2;
      resolve(void 0);
    });
  });

  const src = await new Promise<string>((resolve) => {
    video.addEventListener("seeked", () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL("image/png");

      video.remove();
      canvas.remove();
      resolve(imageData);
    });
  });
  return src;
};
export const openSSOWindow = (
  fromPage: SSOPage = SSOPage.Profile,
  ssoType: SSOType = SSOType.GoogleSSO,
  extraData?: any,
) => {
  const clientId =
    ssoType === SSOType.MicrosoftSSO ? microsoftSSOClientId : googleSSOClientId;
  //const redirect_uri = encodeURIComponent("https://dev-web.agi7.ai/sso");
  const redirect_uri = encodeURIComponent(window.location?.origin + "/sso");
  const requestId = uuid();
  const state = encodeURIComponent(
    JSON.stringify({ fromPage, ssoType, extraData }),
  );
  let authUrl = "";
  if (ssoType === SSOType.MicrosoftSSO) {
    authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&scope=User.Read&redirect_uri=${redirect_uri}&client-request-id=${requestId}&response_mode=fragment&response_type=code&x-client-SKU=msal.js.browser&x-client-VER=3.26.1&client_info=1&nonce=01928ee6-5fba-7ebe-afd0-7af4c5e254a2&state=${state}`;
    //const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&scope=openid%20profile%20User.Read%20offline_access&redirect_uri=${redirect_uri}&client-request-id=01928ee6-5fb3-7d42-9458-1c100ce2527a&response_mode=fragment&response_type=code&x-client-SKU=msal.js.browser&x-client-VER=3.26.1&client_info=1&nonce=01928ee6-5fba-7ebe-afd0-7af4c5e254a2&state=eyJpZCI6IjAxOTI4ZWU2LTVmYjktN2RlNi1hNmUwLTJhNWJlMzQ1MWVhZSIsIm1ldGEiOnsiaW50ZXJhY3Rpb25UeXBlIjoicG9wdXAifX0%3D`;
  } else if (ssoType === SSOType.GoogleSSO) {
    //authUrl = `https://accounts.google.com/o/oauth2/v2/auth?gsiwebsdk=3&client_id=${clientId}&scope=openid%20profile%20email&redirect_uri=storagerelay%3A%2F%2F${redirect_uri}%3Fid%3Dauth92013&prompt=consent&access_type=offline&response_type=code&include_granted_scopes=true&enable_granular_consent=true`;
    authUrl = `https://accounts.google.com/o/oauth2/v2/auth?gsiwebsdk=3&client_id=${clientId}&scope=openid%20profile%20email&redirect_uri=${redirect_uri}&prompt=consent&access_type=offline&response_type=code&include_granted_scopes=true&enable_granular_consent=true&state=${state}`;
  }
  const newWindow = window.open(
    authUrl,
    ssoType, // 窗口名称
    "width=600,height=400,top=300,left=300,menubar=no,toolbar=no,location=no",
  );
  return newWindow;
};

export const validatePhoneNumber = (value) => {
  try {
    const _value = (value.startsWith("+") ? "" : "+") + value;
    const parseRes = parsePhoneNumber(_value);
    if (parseRes.isValid()) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

// input phoneNumber +14155552671
export const parsedPhoneNumber = (
  phoneNumber: string,
  showCountryCode = false,
) => {
  const parsedNumber = parsePhoneNumberFromString(phoneNumber || "");
  if (parsedNumber) {
    const countryCode = parsedNumber.countryCallingCode;

    const nationalNumber = parsedNumber.nationalNumber;

    // format to +1 (415) 555-2671
    const formattedNumber =
      (showCountryCode ? `+${countryCode} ` : "") +
      `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
    console.log(formattedNumber);
    return formattedNumber;
  } else {
    return phoneNumber;
  }
};

export function isCameraExpired(camera) {
  const expired =
    !camera?.expiredAt || Number(camera?.expiredAt) < new Date().getTime();
  return expired;
}

export function errorHandler(error: AppError) {
  if (!error) {
    return error;
  }
  const externalErrorType: ExternalErrorType = {};
  switch (error.name) {
    case "wsError":
      if (error.code === 16) {
        Cookies.remove("token");
        if (window.location.href.indexOf("/login") === -1) {
          window.location.href = "/login";
        }
      }
      break;
    case IdentifiedServerError.EdgeWebrtcConnLimit:
      showToast(1, "NVR connections reached limit,retrying");
      externalErrorType.bandwidthConnFull = true;
      break;
    case IdentifiedServerError.EdgebandwidthLimit:
      externalErrorType.bandwidthConnFull = true;
      showToast(1, "NVR bandwidth reached limit,retrying");
      break;
    case IdentifiedServerError.EdgeSpeedTesting:
      externalErrorType.bandwidthConnFull = true;
      showToast(1, "NVR is testing bandwidth,retrying");
      break;
    case IdentifiedServerError.EdgeDownloaderNotExist:
    case IdentifiedServerError.EdgeDownloaderNoFile:
      externalErrorType.playbackDownloaderNoExist = true;
      break;
    case IdentifiedServerError.AuthLastThirdParty:
      externalErrorType.lastThirdPartWithoutPwd = true;
      break;
  }
  error.setExternalData(externalErrorType);

  // if status exist, it is a http error
  if (Number.isInteger(error?.status)) {
    if (error?.status === 401) {
      Cookies.remove("token");
      if (window.location.href.indexOf("/login") === -1) {
        window.location.href = "/login";
      }
    } else if (error?.status === 403) {
      showToast(1, "Access Denied");
    } else if (error?.status >= 400 && error?.status <= 500 && error?.message) {
      showToast(1, `${error?.message}(${error?.name})`);
    }
    if (error?.status !== 308) {
      console.error(error.toString());
    }
  }
  return error;
}

const nvrSupportWebRtcMap = new Map();
export const isNvrSupportSinglePc = async (nvrId, orgId?) => {
  if (nvrSupportWebRtcMap.has(nvrId)) {
    return nvrSupportWebRtcMap.get(nvrId);
  }

  const response = await sendHttpRequest({
    method: "get",
    url: `/nvr/nvrs/${nvrId}/support_single_pc`,
    headers: {
      "x-org-id": orgId ?? Cookies.get("orgId"),
    },
  });
  let supportNewWebrtc = false;
  if (response.status === 200) {
    supportNewWebrtc = response.data?.support;
  }
  nvrSupportWebRtcMap.set(nvrId, supportNewWebrtc);
  return supportNewWebrtc;
};

const deviceSupportSinglePc = new Map();
export const isSupportSinglePcV2 = async (deviceId, orgId?) => {
  if (deviceSupportSinglePc.has(deviceId)) {
    return deviceSupportSinglePc.get(deviceId);
  }

  const response = await sendHttpRequest({
    method: "get",
    url: `/device/devices/${deviceId}/support_single_pc`,
    headers: {
      "x-org-id": orgId ?? Cookies.get("orgId"),
    },
  });
  let supportSinglePc = false;
  if (response.status === 200) {
    supportSinglePc = response.data?.support;
  }
  deviceSupportSinglePc.set(deviceId, supportSinglePc);
  return supportSinglePc;
};

export function safeParseJSON(jsonString: string) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    return null;
  }
}
export function toPascalCase(role: USER_ROLE): string {
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // 首字母大写
    .join(" ");
}
